// src/pages/ChatBot/query-logs/inspect-stats.tsx
//
// Statistics card for the Inspect modal — a compact, chart-free readout derived
// from a logged query's retrieval trace: how many chunks survived each pipeline
// stage, the min/median/max of every scored axis, and the token budget handed to
// the LLM (with what the final-topK gate cut). Sits between the answer card and
// the retrieval-trace explorer. Pure presentation over the ChunkTrace list plus
// the per-chunk tokenizer counts; the whole card is hidden when the query has no
// trace.

import type { ChunkTrace } from './types';
import { countChunksAtStage } from './inspect-trace-helpers';
import type { TraceStage } from './inspect-trace-helpers';

// Scored axes we report distributions for. Vector/fused are small decimals shown
// to 3dp; rerank is the LLM judge's 0-100 scale shown to 1dp. An axis with no
// scores is skipped entirely.
type ScoreAxis = 'vector' | 'fused' | 'rerank';

const SCORE_AXES: { axis: ScoreAxis; label: string }[] = [
  { axis: 'vector', label: 'Vector' },
  { axis: 'fused', label: 'Fused' },
  { axis: 'rerank', label: 'Rerank' },
];

// The stage-count row, in pipeline order. Labels match the trace explorer's lens.
const STAGE_ROW: { stage: TraceStage; label: string }[] = [
  { stage: 'vector', label: 'Vector' },
  { stage: 'text', label: 'Text' },
  { stage: 'fused', label: 'Fused' },
  { stage: 'rerank', label: 'Reranked' },
  { stage: 'final', label: 'Final' },
];

interface AxisDistribution {
  axis: ScoreAxis;
  label: string;
  min: number;
  median: number;
  max: number;
}

interface TokenBudget {
  hasFinal: boolean;
  finalTokens: number;
  hasCut: boolean;
  cutTokens: number;
  partial: boolean; // some cut/final chunk has no token count (no longer resolves)
}

interface RetrievalOverlap {
  fusedCount: number; // chunks that reached the fused pool
  bothCount: number;  // of those, how many were surfaced by BOTH the vector and keyword axes
}

// How much the vector and keyword axes agreed: of the fused pool, how many chunks appeared in BOTH
// searches. A low count (or 0) tells an admin the keyword axis contributed little — or, with the
// text-search-failed banner, nothing at all.
function computeRetrievalOverlap(chunks: ChunkTrace[]): RetrievalOverlap {
  const fused = chunks.filter((chunk) => chunk.rank.fused !== undefined);
  const bothCount = fused.filter(
    (chunk) => chunk.rank.vector !== undefined && chunk.rank.text !== undefined,
  ).length;
  return { fusedCount: fused.length, bothCount };
}

// Median of an already-ascending array (caller sorts). Even length averages the
// two middle values.
function median(sortedAscending: number[]): number {
  const mid = Math.floor(sortedAscending.length / 2);
  return sortedAscending.length % 2 === 0
    ? (sortedAscending[mid - 1] + sortedAscending[mid]) / 2
    : sortedAscending[mid];
}

function formatScore(value: number, axis: ScoreAxis): string {
  if (axis === 'rerank') return value.toFixed(1);
  return value.toFixed(3);
}

// One distribution per scored axis that actually has scores; an axis with no
// scores is skipped entirely.
function computeAxisDistributions(chunks: ChunkTrace[]): AxisDistribution[] {
  const distributions: AxisDistribution[] = [];
  for (const { axis, label } of SCORE_AXES) {
    const scores = chunks
      .map((chunk) => chunk.score[axis])
      .filter((score): score is number => score !== undefined)
      .sort((a, b) => a - b);
    if (scores.length === 0) continue;
    distributions.push({
      axis,
      label,
      min: scores[0],
      median: median(scores),
      max: scores[scores.length - 1],
    });
  }
  return distributions;
}

// Token budget for the LLM handoff: total tokens of the FINAL chunks, plus what
// the budget/topK gate cut. The gate is the rerank stage when the pipeline
// reranked at all, else the fused stage — cut chunks reached that gate but never
// got a final rank. A chunk whose tokenizer count is absent (superseded) can't
// be summed, so any such chunk flips `partial`.
function computeTokenBudget(
  chunks: ChunkTrace[],
  chunkTokens: Record<string, number>,
): TokenBudget {
  const hasRerankStage = chunks.some((chunk) => chunk.rank.rerank !== undefined);
  const gateAxis: 'rerank' | 'fused' = hasRerankStage ? 'rerank' : 'fused';

  const finalChunks = chunks.filter((chunk) => chunk.rank.final !== undefined);
  const cutChunks = chunks.filter(
    (chunk) => chunk.rank[gateAxis] !== undefined && chunk.rank.final === undefined,
  );

  let finalTokens = 0;
  let cutTokens = 0;
  let partial = false;

  for (const chunk of finalChunks) {
    const tokens = chunkTokens[chunk.chunkId];
    if (tokens === undefined) partial = true;
    else finalTokens += tokens;
  }
  for (const chunk of cutChunks) {
    const tokens = chunkTokens[chunk.chunkId];
    if (tokens === undefined) partial = true;
    else cutTokens += tokens;
  }

  return {
    hasFinal: finalChunks.length > 0,
    finalTokens,
    hasCut: cutChunks.length > 0,
    cutTokens,
    partial,
  };
}

interface InspectStatsProps {
  chunks: ChunkTrace[];
  chunkTokens: Record<string, number>;
}

export default function InspectStats({ chunks, chunkTokens }: InspectStatsProps) {
  // Resilient to an empty trace — the neighboring section already guards this,
  // but keep the card self-hiding so it's safe to mount unconditionally.
  if (chunks.length === 0) return null;

  const distributions = computeAxisDistributions(chunks);
  const budget = computeTokenBudget(chunks, chunkTokens);
  const overlap = computeRetrievalOverlap(chunks);

  return (
    <div className="qli-card">
      <div className="qli-micro-label qli-stats-heading">Statistics</div>

      <div className="qli-stats-block">
        <div className="qli-stats-caption">Chunks per stage</div>
        <div className="qli-stats-stages">
          {STAGE_ROW.map(({ stage, label }, index) => (
            <span key={stage}>
              {index > 0 && <span className="qli-stats-dot"> · </span>}
              <span className="qli-stats-stage-label">{label}</span>{' '}
              <span className="qli-stats-stage-count">{countChunksAtStage(chunks, stage)}</span>
            </span>
          ))}
        </div>
      </div>

      {overlap.fusedCount > 0 && (
        <div className="qli-stats-block">
          <div className="qli-stats-caption">Retrieval overlap</div>
          <div className="qli-stats-overlap">
            <span className="qli-stats-stage-count">{overlap.bothCount}</span> of{' '}
            <span className="qli-stats-stage-count">{overlap.fusedCount}</span> fused chunks appeared in
            BOTH searches (vector + keyword)
          </div>
        </div>
      )}

      {distributions.length > 0 && (
        <div className="qli-stats-block">
          <div className="qli-stats-caption">Score range · min / median / max</div>
          <div className="qli-stats-scores">
            {distributions.map((dist) => (
              <div key={dist.axis} className="qli-stats-score-row">
                <span className="qli-stats-score-axis">{dist.label}</span>
                <span className="qli-stats-score-values">
                  {formatScore(dist.min, dist.axis)} / {formatScore(dist.median, dist.axis)} /{' '}
                  {formatScore(dist.max, dist.axis)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {budget.hasFinal && (
        <div className="qli-stats-block">
          <div className="qli-stats-caption">LLM token budget</div>
          <div className="qli-stats-tokens">
            {budget.finalTokens.toLocaleString()} tokens to the LLM
            {budget.hasCut && <> (+ {budget.cutTokens.toLocaleString()} cut)</>}
            {budget.partial && (
              <span className="qli-stats-partial">
                {' '}
                (partial — some chunks no longer resolve)
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
